import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import PageTitle from '../../layouts/PageTitle';
import { useStaticCollections } from '../../../context/StaticCollections';
import { useAdminCollections } from '../../../context/AdminCollections';
import { useLoggedInUserContextProvider } from '../../../context/LoggedInUserContext';
import { addCmseCommitteeToSectionalCommitteeArray } from '../../../utils/addCmseCommitteeToSectionalCommitteeArray';
import { getNominationRoundYear } from '../../../utils/getNominationRoundYear';

import { useUserPermissions } from '../../hooks/useUserPermissions';

import FilteringTable from '../Table/FilteringTable/FilteringTable';
import ColumnFilter from '../Table/FilteringTable/ColumnFilter';

import { getCandidateScoresTableData } from '../../../utils/getCandidateScoresTableData';
import { filteringTableGetTableColumns } from '../../../utils/filteringTableGetTableColumns';
import { getScoringMatrixConfig } from '../../../utils/mockScoringData';
import { filterObserverCommittees } from '../../../utils/filterObserverCommittees';
import { CM_PRIMARY_ID } from '../../../utils/constants';

import {
  AverageScoreCell,
  CommentsCell,
  ScoreCell,
} from '../CandidateScoresReportCell';

const baseTableColumns = {
  'Candidate Name': 'candidateName',
  status: 'nominationStatusType',
  Committee: 'committeeName',
  'Election type': 'electionType',
  Scorer: 'scorerName',
  'Scorer Role': 'scorerRole',
};

const allScoringColumns = {
  'Scientific Excellence': 'scientificExcellence',
  'International Profile': 'internationalProfile',
  'Connection to Australian': 'connectionToAustralian',
  'Scientific Achievement': 'scientificLeadership',
  'National Profile': 'nationalProfile',
  'Leadership Mentorship': 'leadershipMentorship',
  'Service to Science': 'serviceToScience',
  'Academy Vision': 'academyVision',
};

const endTableColumns = {
  'Total Score': 'totalScore',
  Mean: 'averageScore',
  Comments: 'comments',
  'Created at': 'createdAt',
  'Updated at': 'updatedAt',
  'Published at': 'publishedAt',
};

const getElectionTypeOptions = (
  isSpecialCommittee,
  isRegularCommittee,
  useOrdinaryElectionLabel,
) => {
  const electionTypeOptions = [
    { value: 'all', label: 'Summary' },
    {
      value: '2',
      label: useOrdinaryElectionLabel ? 'Ordinary Election' : 'Score Breakdown',
      specialOnly: false,
    },
    { value: '1', label: 'Corresponding Membership', regularOnly: false },
    { value: '3', label: 'Special Election', regularOnly: false },
  ];

  return electionTypeOptions
    .filter((option) => {
      if (isRegularCommittee) {
        return option.value === 'all' || option.value === '2';
      }
      if (isSpecialCommittee) {
        return option.value !== '2';
      }
      return true;
    })
    .map((option) => ({
      value: option.value,
      label: option.label,
    }));
};

const getDefaultElectionTypeForCommittee = (userCommittee) => {
  if (!userCommittee) return 'all';

  if (userCommittee.id === CM_PRIMARY_ID) return 'all';
  if (userCommittee.id === 15) return '3';
  if (userCommittee.id >= 1 && userCommittee.id <= 13) return '2';

  return 'all';
};

const CommitteeCardHeader = ({ committeeName }) => (
  <div className="card-header text-center">
    <h4 className="card-title mb-2">
      <strong>{committeeName}</strong>
    </h4>
    <h6 className="text-muted">
      {getNominationRoundYear(new Date())} ROUND - Candidate Scores Report
    </h6>
  </div>
);

CommitteeCardHeader.propTypes = {
  committeeName: PropTypes.string.isRequired,
};

const ElectionTypeButtons = ({
  selectedElectionType,
  onElectionTypeChange,
  isSpecialCommittee = false,
  isRegularCommittee = false,
  useOrdinaryElectionLabel = false,
}) => {
  const options = getElectionTypeOptions(
    isSpecialCommittee,
    isRegularCommittee,
    useOrdinaryElectionLabel,
  );

  return (
    <div className="d-flex justify-content-center mt-xl-5 mb-4">
      <div className="btn-group" role="group" aria-label="Election type filter">
        {options.map((option) => (
          <button
            key={option.value}
            type="button"
            className={`btn ${
              selectedElectionType === option.value
                ? 'btn-primary'
                : 'btn-outline-primary'
            }`}
            onClick={() => onElectionTypeChange(option.value)}
          >
            {option.label}
          </button>
        ))}
      </div>
    </div>
  );
};

ElectionTypeButtons.propTypes = {
  selectedElectionType: PropTypes.string.isRequired,
  onElectionTypeChange: PropTypes.func.isRequired,
  isSpecialCommittee: PropTypes.bool,
  isRegularCommittee: PropTypes.bool,
  useOrdinaryElectionLabel: PropTypes.bool,
};

const renderAverageScoreCell = (info) => {
  const value = info.getValue?.() ?? info.row.original.averageScore;
  return <AverageScoreCell value={value} />;
};

const renderScoreCell = (info) => {
  const value = info.getValue?.() ?? info.row.original[info.column.id];
  return <ScoreCell value={value} />;
};

const renderCommentsCell = (info) => {
  const value = info.getValue?.() ?? info.row.original.comments;
  return <CommentsCell value={value} />;
};

const CandidateScoresReport = () => {
  const [notificationMessage, setNotificationMessage] = useState('');
  const [notificationTitle, setNotificationTitle] = useState('');
  const [selectedCommittee, setSelectedCommittee] = useState(null);

  const [selectedElectionType, setSelectedElectionType] = useState('all');
  const [candidateScoresTableData, setCandidateScoresTableData] = useState([]);
  const [candidateScoresTableColumns, setCandidateScoresTableColumns] =
    useState([]);

  const {
    sectionalCommitteesArray,
    fellowsArray,
    nonAasRefereesArray,
    electionTypes,
    nominationStatusTypes,
    isLoading: staticLoading,
  } = useStaticCollections();

  const {
    candidateArray,
    committeeArray,
    candidateScoresArray,
    nominationArray,
    isLoading: adminLoading,
  } = useAdminCollections();

  const { userDetails } = useLoggedInUserContextProvider();

  const {
    isAdmin,
    hasAccess,
    userCommittee,
    userCommittees,
    isCouncilMember,
    isSCSecretary,
    isSCObserver,
  } = useUserPermissions(userDetails, committeeArray);

  const sectionalCommitteesWithCmse = useMemo(
    () =>
      addCmseCommitteeToSectionalCommitteeArray(sectionalCommitteesArray, true),
    [sectionalCommitteesArray],
  );

  const getTableColumns = useMemo(
    () => (electionType) => {
      const scoringColumns = {};

      if (electionType === 'all') {
        return {
          ...baseTableColumns,
          Total: 'totalScore',
          Mean: 'averageScore',
          Comments: 'comments',
          'Created at': 'createdAt',
          'Updated at': 'updatedAt',
          'Published at': 'publishedAt',
        };
      }

      if (electionType !== 'all') {
        const matrixConfig = getScoringMatrixConfig(parseInt(electionType));
        matrixConfig.criteria.forEach((criterion) => {
          const columnKey = Object.keys(allScoringColumns).find(
            (key) => allScoringColumns[key] === criterion.key,
          );
          if (columnKey) {
            scoringColumns[columnKey] = criterion.key;
          }
        });
      }

      return {
        ...baseTableColumns,
        ...scoringColumns,
        ...endTableColumns,
      };
    },
    [],
  );

  const currentTableColumns = useMemo(
    () => getTableColumns(selectedElectionType),
    [getTableColumns, selectedElectionType],
  );

  const processedColumns = useMemo(() => {
    const tableColumns = filteringTableGetTableColumns(
      currentTableColumns,
      ColumnFilter,
    );

    return tableColumns.map((column) => {
      if (column.id === 'averageScore') {
        return {
          ...column,
          cell: renderAverageScoreCell,
        };
      }

      if (column.id === 'comments') {
        return {
          ...column,
          cell: renderCommentsCell,
        };
      }

      if (
        [
          'scientificExcellence',
          'internationalProfile',
          'connectionToAustralian',
          'scientificLeadership',
          'nationalProfile',
          'leadershipMentorship',
          'serviceToScience',
          'academyVision',
          'totalScore',
        ].includes(column.id)
      ) {
        return {
          ...column,
          cell: renderScoreCell,
        };
      }
      return column;
    });
  }, [currentTableColumns]);

  const tableData = useMemo(() => {
    if (
      candidateScoresArray &&
      candidateScoresArray.length >= 0 &&
      committeeArray.length > 0
    ) {
      const { allTableData } = getCandidateScoresTableData(
        selectedCommittee,
        candidateScoresArray,
        committeeArray,
        sectionalCommitteesWithCmse,
        fellowsArray,
        nonAasRefereesArray,
        nominationArray,
        candidateArray,
        electionTypes,
        nominationStatusTypes,
        selectedElectionType,
      );

      if (
        (isSCObserver || isCouncilMember) &&
        userCommittees.length > 0 &&
        !selectedCommittee
      ) {
        const observerCommitteeIds = userCommittees.map((c) => c.id);
        return filterObserverCommittees(allTableData, observerCommitteeIds);
      }

      return allTableData;
    }
    return [];
  }, [
    candidateScoresArray,
    committeeArray,
    selectedCommittee,
    selectedElectionType,
    sectionalCommitteesWithCmse,
    candidateArray,
    nominationArray,
    fellowsArray,
    nonAasRefereesArray,
    electionTypes,
    isSCObserver,
    userCommittees,
  ]);

  useEffect(() => {
    if (!isAdmin && !isSCObserver && userCommittee) {
      if (userCommittee.id >= 1 && userCommittee.id <= 13) {
        setSelectedElectionType('all');
      } else if (
        userCommittee.id === CM_PRIMARY_ID ||
        userCommittee.id === 15
      ) {
        const defaultElectionType =
          getDefaultElectionTypeForCommittee(userCommittee);
        setSelectedElectionType(defaultElectionType);
      }
    }
  }, [isAdmin, isSCObserver, userCommittee]);

  useEffect(() => {
    setCandidateScoresTableColumns(processedColumns);
  }, [processedColumns]);

  useEffect(() => {
    setCandidateScoresTableData(tableData);
  }, [tableData]);

  useEffect(() => {
    if (!hasAccess) {
      setNotificationMessage(
        'You do not have permission to view candidate scores reports.',
      );
      setNotificationTitle('Access Denied');
    }
  }, [hasAccess]);

  useEffect(() => {
    if (!isAdmin && !isSCObserver && userCommittee && !selectedCommittee) {
      setSelectedCommittee(userCommittee.id);
    }
  }, [
    isAdmin,
    isCouncilMember,
    isSCObserver,
    userCommittee,
    selectedCommittee,
  ]);

  const handleElectionTypeChange = (electionType) => {
    setSelectedElectionType(electionType);
  };

  const renderReportContent = () => (
    <div className="mt-xl-5">
      <FilteringTable
        componentName="CandidateScoresReport"
        columns={candidateScoresTableColumns}
        data={candidateScoresTableData}
        urlString=""
        buttonText=""
        isLoading={staticLoading || adminLoading}
      />
    </div>
  );

  if (!hasAccess) {
    return (
      <PageTitle
        motherMenu="Reports"
        activeMenu="Candidate Scores"
        notificationMessage={notificationMessage}
        notificationTitle={notificationTitle}
      />
    );
  }

  const shouldShowButtons =
    isAdmin ||
    isSCSecretary ||
    isSCObserver ||
    (userCommittee && userCommittee.id >= 1 && userCommittee.id <= 15);
  const isSpecialCommittee =
    userCommittee &&
    (userCommittee.id === CM_PRIMARY_ID || userCommittee.id === 15);
  const isRegularCommittee =
    userCommittee && userCommittee.id >= 1 && userCommittee.id <= 13;

  const useOrdinaryElectionLabel = isAdmin || isSCSecretary;

  return (
    <>
      <PageTitle
        motherMenu="Reports"
        activeMenu="Candidate Scores"
        notificationTitle={notificationTitle}
      />
      {shouldShowButtons && (
        <ElectionTypeButtons
          selectedElectionType={selectedElectionType}
          onElectionTypeChange={handleElectionTypeChange}
          isSpecialCommittee={isSpecialCommittee}
          isRegularCommittee={isRegularCommittee}
          useOrdinaryElectionLabel={useOrdinaryElectionLabel}
        />
      )}

      {renderReportContent()}
    </>
  );
};

export default CandidateScoresReport;
